import React, { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/utils";
import Text from "@/components/ui/text";
import SearchInput from "@/components/ui/search-input";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import { Filters, FiltersSetter } from "@/services/api/types";
import UserAvatar from "@/components/controls/UserAvatar";
import { useUsers } from "@/entities/users/hooks";
import Checkbox from "@/components/ui/checkbox";
import FilterMenuItem from "@/components/controls/ToolbarActions/ToolbarFilterButton/FilterMenuItem";
import {
  useFilterOptions,
  useFilterToggleWithNotIn
} from "@/hooks/useFilterOptions";

interface Props {
  filterOption: DataTableFilter;
  filters: Filters;
  setFilters: FiltersSetter;
  onClose?: () => void;
}

const FilterSubmenu: React.FC<Props> = ({
  filterOption,
  filters,
  setFilters,
  onClose
}) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const filterKey = filterOption.key;

  const {
    combinedOptions,
    selectedValues,
    useServerSearch,
    isLoading,
    onSearch,
    showAvatar,
    shouldShowSearch
  } = useFilterOptions({
    filterOption,
    filters,
    searchValue,
    alwaysEnabled: true
  });

  const toggleValue = useFilterToggleWithNotIn(filterKey, setFilters);

  const userIds = useMemo(
    () => (showAvatar ? combinedOptions.map((o) => String(o.value)) : []),
    [showAvatar, combinedOptions]
  );
  const { users } = useUsers(userIds, { enabled: showAvatar });
  const usersMap = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      if (useServerSearch && onSearch) {
        onSearch(value);
      }
    },
    [useServerSearch, onSearch]
  );

  return (
    <div className="flex flex-col gap-2 min-w-[200px] max-h-[300px]">
      {shouldShowSearch && (
        <div className="sticky top-0 bg-surface-elevation2 z-10">
          <SearchInput
            placeholder={t("search")}
            value={searchValue}
            onChange={handleSearchChange}
            hideIcon
            clearButtonSize="xs"
            clearButtonColor="secondary"
          />
        </div>
      )}
      <div className="flex flex-col overflow-y-auto">
        {isLoading ? (
          <Text variant="bodyTertiary" className="px-2 py-4 text-center">
            {t("loading")}
          </Text>
        ) : (
          <>
            {combinedOptions.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <FilterMenuItem
                  key={String(option.value)}
                  layout="compact"
                  onClick={() => {
                    toggleValue(option.value);
                    onClose?.();
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    className="mr-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleValue(option.value);
                    }}
                  />
                  {option.icon && option.icon}
                  {showAvatar &&
                    !option.icon &&
                    usersMap.has(String(option.value)) && (
                      <UserAvatar
                        size="M"
                        fallbackClassName="bg-gradient-to-t from-slate-600 to-tertiary"
                        userData={usersMap.get(String(option.value))}
                      />
                    )}
                  <Text
                    variant={option?.labelVariant || "body"}
                    className={cn(
                      "flex-1",
                      option.isNoValue && "italic text-tertiary"
                    )}
                  >
                    {option.label}
                  </Text>
                </FilterMenuItem>
              );
            })}
            {combinedOptions.length === 0 && (
              <Text variant="bodyTertiary" className="px-2 py-4 text-center">
                {t("tables.emptyState.title")}
              </Text>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FilterSubmenu;
